import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import type { GoalConfig } from "../../../shared/goals/config";
import type { Block } from "../../../shared/goals/messages";
import type { Plan } from "../../../shared/goals/plan";
import { Icon } from "../../components/ui/Icon";
import { Rich } from "../../components/ui/Rich";
import type { ActionView, Need, ReadyLite, Results } from "../../lib/ccApi";
import { MoneyCard, PeopleCard, PeopleRows, ReadyCard, SettingsCard, TestsCard } from "./Cards";
import { NeedRows } from "./Needs";
import { RunBlock, runSummary, useRunRow } from "./RunCard";
import { useMayDecide } from "../../context/AuthContext";

/** Everything a message's blocks need from the chat around them. */
export interface BlockCtx {
  goalId: string;
  config: GoalConfig | null;
  plan: Plan | null;
  version: number;
  state: string;
  /** What waits for the owner now. */
  needs: Need[];
  /** Actions known by id, in any state (a review's proposals, a change in an answer). */
  actions: Map<string, ActionView>;
  /** Ask for these actions to be read, when they are not known yet. */
  want: (ids: string[]) => void;
  results: Results | null;
  ready: ReadyLite | null;
  /** The message that holds the newest card of each live part; older ones show as still copies. */
  liveAt: Map<string, string>;
  /** When the next review is, in words ("tomorrow at 9:00"), from `GET /home`. */
  nextReview: string | null;
  spentCents: number;
  openNeed: (id: string) => void;
  decide: (n: Need, d: "approve" | "skip") => Promise<boolean>;
  say: (m: string, ok?: boolean) => void;
  reload: () => Promise<void>;
  pause: (on: boolean) => void;
}

/** The chip a decided action shows, from its state now. */
export function decidedLabel(a: ActionView): string {
  if (a.state === "skipped") return "Skipped";
  if (a.state === "executed") return a.kind === "email.reply" ? "Sent" : "Approved";
  if (a.state === "approved" || a.state === "executing") return "Approved · running";
  if (a.state === "failed" || a.state === "refused") return "Could not run";
  return "No longer needed";
}

/** Where a goal.change applies in Edit goal: the step that holds it. */
export function stepOf(patch: unknown): string | null {
  const keys = patch && typeof patch === "object" ? Object.keys(patch) : [];
  if (keys.some((k) => ["sentence", "result", "money"].includes(k))) return "goal";
  if (keys.includes("library")) return "playbook";
  if (keys.includes("channels")) return "channel";
  return null;
}

function Diff({ b, ctx }: { b: Extract<Block, { type: "diff" }>; ctx: BlockCtx }) {
  const [busy, setBusy] = useState(false);
  const id = b.actionId;
  const need = id ? ctx.needs.find((n) => n.id === id) : undefined;
  const action = id ? ctx.actions.get(id) : undefined;
  const open = need ?? (action?.state === "proposed" ? action : undefined);
  const mayDecide = useMayDecide();
  const step = open?.kind === "goal.change" ? stepOf(open.payload.patch) : null;
  const version = typeof action?.result?.version === "number" ? action.result.version : null;
  const decide = async (d: "approve" | "skip") => {
    if (!open) return;
    setBusy(true);
    await ctx.decide(open, d);
    setBusy(false);
  };
  return (
    <>
      <div className="diff">
        {b.lines.map((l, i) => (
          <div className={`dl ${l.kind}`} key={i}>
            {l.text}
          </div>
        ))}
      </div>
      {id && (
        <div className="m-act">
          {open ? (
            mayDecide(open) && (
              <>
                <button type="button" className="btn sm" disabled={busy} onClick={() => void decide("approve")}>
                  Apply
                </button>
                <button type="button" className="btn sm ghost" disabled={busy} onClick={() => void decide("skip")}>
                  Not now
                </button>
                {step && (
                  <Link className="btn sm ghost" to={`/goals/${encodeURIComponent(ctx.goalId)}/edit?step=${step}`}>
                    <Icon name="pen" />
                    Edit goal
                  </Link>
                )}
              </>
            )
          ) : action ? (
            action.state === "skipped" ? (
              <span className="chip">No change</span>
            ) : action.state === "executed" ? (
              <span className="chip ok">
                <Icon name="check" />
                {version ? `Changed · plan version ${version}` : "Applied"}
              </span>
            ) : (
              <span className="chip">{decidedLabel(action)}</span>
            )
          ) : null}
        </div>
      )}
    </>
  );
}

function Live({ part, mid, ctx }: { part: string; mid: string; ctx: BlockCtx }) {
  const kind = part === "results" ? "money" : part === "emails" ? "ads" : part;
  const still = ctx.liveAt.get(kind) !== mid;
  let body: ReactNode = null;
  if (kind === "settings" && ctx.config)
    body = (
      <SettingsCard
        goalId={ctx.goalId}
        config={ctx.config}
        version={ctx.version}
        state={ctx.state}
        spentCents={ctx.spentCents}
        editHref={`/goals/${encodeURIComponent(ctx.goalId)}/edit`}
        say={ctx.say}
        onSaved={ctx.reload}
        onPause={ctx.pause}
      />
    );
  else if (kind === "money" && ctx.config) body = ctx.results ? <MoneyCard config={ctx.config} results={ctx.results} /> : <p className="cc-fine">No results have been read yet.</p>;
  else if (kind === "tests") body = <TestsCard plan={ctx.plan} results={ctx.results} config={ctx.config} />;
  else if (kind === "needs") body = <NeedRows rows={ctx.needs} done={[]} onOpen={ctx.openNeed} />;
  else if (kind === "ads" && ctx.ready) body = <ReadyCard goalId={ctx.goalId} ready={ctx.ready} />;
  else if (kind === "people") body = <PeopleCard goalId={ctx.goalId} />;
  if (!body) return null;
  return (
    <div className={`cc-host${still ? " cc-still" : ""}`} inert={still || undefined}>
      {body}
    </div>
  );
}

/** A review's need rows: what still waits, and what was already decided, as it stands now. */
function NeedsBlock({ runId, ctx }: { runId: string | null; ctx: BlockCtx }) {
  const run = useRunRow(runId);
  const ids = runId ? (runSummary(run).needs ?? null) : null;
  const { want } = ctx;
  const key = ids?.join(",") ?? "";
  useEffect(() => {
    if (key) want(key.split(","));
  }, [key, want]);
  if (!ids) return <NeedRows rows={ctx.needs} done={[]} onOpen={ctx.openNeed} />;
  const rows = ctx.needs.filter((n) => ids.includes(n.id));
  const done = ids
    .map((id) => ctx.actions.get(id))
    .filter((a): a is ActionView => !!a && a.state !== "proposed" && !rows.some((r) => r.id === a.id))
    .map((a) => ({ need: a, label: decidedLabel(a) }));
  return <NeedRows rows={rows} done={done} onOpen={ctx.openNeed} />;
}

/** A message's blocks, in order: the agent picks them to answer best (shared/goals/messages.ts). */
export function Blocks({ blocks, mid, runId, ctx }: { blocks: Block[]; mid: string; runId: string | null; ctx: BlockCtx }) {
  return (
    <>
      {blocks.map((b, i) => {
        switch (b.type) {
          case "text":
            return <Rich key={i} text={b.text} className="cc-rich" block />;
          case "looked":
            return (
              <details className="cc-look" key={i}>
                <summary>
                  <span className="cc-ck">
                    <Icon name="check" />
                  </span>
                  <span>
                    Looked at {b.items.length} {b.items.length > 1 ? "sources" : "source"}
                  </span>
                </summary>
                <ul>
                  {b.items.map((x, j) => (
                    <li key={j}>
                      <span className="cc-ck">
                        <Icon name="check" />
                      </span>
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </details>
            );
          case "bars": {
            const max = Math.max(1, ...b.rows.map((r) => r.value));
            return (
              <div className="cc-card" key={i}>
                <h4>{b.title}</h4>
                <div className="cc-bars">
                  {b.rows.map((r, j) => (
                    <div className="cc-bar" key={j}>
                      <span className="n">{r.label}</span>
                      <span className="cc-track">
                        <i className={r.muted ? "lo" : ""} style={{ width: `${Math.max(2, (r.value / max) * 100)}%` }} />
                      </span>
                      <span className="v">{r.note}</span>
                    </div>
                  ))}
                </div>
                {b.footnote && <p className="cc-fine">{b.footnote}</p>}
              </div>
            );
          }
          case "people":
            return <PeopleRows key={i} goalId={ctx.goalId} rows={b.rows} footnote={b.footnote} say={ctx.say} />;
          case "diff":
            return <Diff key={i} b={b} ctx={ctx} />;
          case "list":
            return (
              <ul className="bl" key={i}>
                {b.items.map((x, j) => (
                  <li key={j}>{x}</li>
                ))}
              </ul>
            );
          case "needs":
            return (
              <div key={i}>
                <NeedsBlock runId={runId} ctx={ctx} />
              </div>
            );
          case "live":
            return <Live key={i} part={b.part} mid={mid} ctx={ctx} />;
          case "run":
            return <RunBlock key={i} goalId={ctx.goalId} runId={b.runId} title={b.title} next={ctx.nextReview} />;
          case "ask":
            return (
              <div className="cc-card cc-ask" key={i}>
                {b.questions.map((q, j) => (
                  <p key={j}>{q}</p>
                ))}
              </div>
            );
          case "link":
            return (
              <div className="m-act" key={i}>
                {/^https?:\/\//.test(b.to) ? (
                  <a className="btn sm ghost" href={b.to} target="_blank" rel="noopener noreferrer">
                    {b.label}
                    <Icon name="right" />
                  </a>
                ) : (
                  <Link className="btn sm ghost" to={b.to}>
                    {b.label}
                    <Icon name="right" />
                  </Link>
                )}
              </div>
            );
          default:
            return null;
        }
      })}
    </>
  );
}
